import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  createAdminUser,
  fetchAdminUsers,
  resetStatus,
  updateAdminUser,
  deleteAdminUser
} from "../../../slice/adminUser/adminUserSlice";


export default function AddUser() {
  const dispatch = useDispatch();

  const {
    list = [],
    loading,
    success,
    error
  } = useSelector((state) => state.adminUser);

  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({
    username: "",
    password: "",
    role: 2  // default: 2 = admin
  });
  const [editId, setEditId] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  /* =============================
     FETCH ADMIN USERS ON LOAD
  ============================= */
  useEffect(() => {
    dispatch(fetchAdminUsers());
  }, [dispatch]);

  /* =============================
     AFTER CREATE/UPDATE SUCCESS
  ============================= */
  useEffect(() => {
    if (success) {
      setShowModal(false);
      setEditId(null);
      dispatch(fetchAdminUsers());
      dispatch(resetStatus());
      setFormData({ username: "", password: "", role: 2 });
    }
  }, [success, dispatch]);

  const handleChange = (e) => {
    const value = e.target.name === "role" ? Number(e.target.value) : e.target.value;
    setFormData({ ...formData, [e.target.name]: value });
  };

  const handleSubmit = () => {
    if (editId) {
      dispatch(updateAdminUser({ id: editId, data: formData }));
    } else {
      dispatch(createAdminUser(formData));
    }
  };

  const confirmDelete = () => {
    dispatch(deleteAdminUser(deleteId));
    setShowDeleteModal(false);
  };

  const closeAllModals = () => {
    setShowDeleteModal(false);
    setShowModal(false);
    setEditId(null);
    setFormData({ username: "", password: "", role: 2 });
  };

  const getRoleLabel = (role) => {
    switch (Number(role)) {
      case 1: return "Super Admin";
      case 2: return "Admin";
      default: return "Unknown";
    }
  };

  return (
    <div className="user-list-container">
      {/* HEADER */}
      <div className="user-list-header">
        <h2 className="user-list-title">Admin User List</h2>
        <button
          className="btn-add-user"
          onClick={() => {
            setEditId(null);
            setFormData({ username: "", password: "", role: 2 });
            setShowModal(true);
          }}
        >
          Add Admin
        </button>
      </div>

      {/* TABLE */}
      <div className="table-wrapper">
        <table className="user-table">
          <thead>
            <tr>
              <th>SI</th>
              <th>Username</th>
              <th>Role</th>
              <th>Action</th>
            </tr>
          </thead>

          <tbody>
            {list.length > 0 ? (
              list.map((admin, index) => (
                <tr key={admin._id}>
                  <td>{index + 1}</td>
                  <td>{admin.username}</td>
                  <td>{getRoleLabel(admin.role)}</td>
                  <td>
                    <div className="d-flex gap-2">
                      <div
                        className="btn btn-soft-primary btn-sm"
                        onClick={() => {
                          setEditId(admin._id);
                          setFormData({
                            username: admin.username,
                            password: "",
                            role: Number(admin.role)
                          });
                          setShowModal(true);
                        }}
                      >
                        <iconify-icon icon="solar:pen-2-broken" />
                      </div>
                      <div
                        className="btn btn-soft-danger btn-sm"
                        onClick={() => {
                          setDeleteId(admin._id);
                          setShowDeleteModal(true);
                        }}
                      >
                        <iconify-icon icon="solar:trash-bin-minimalistic-2-broken" />
                      </div>
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="4" className="no-data">
                  No admin users found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* DELETE MODAL */}
      {showDeleteModal && (
        <div className="modal-overlay" onClick={closeAllModals}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-body text-center">
              Are you sure you want to delete this user?
            </div>
            <div className="modal-footer">
              <button className="btn cusClose" onClick={closeAllModals}>
                Cancel
              </button>
              <button className="btn cuscancel" onClick={confirmDelete}>
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ADD / EDIT MODAL */}
      {showModal && (
        <div className="modal-overlay" onClick={closeAllModals}>
          <div
            className="modal-content"
            onClick={(e) => e.stopPropagation()}
            style={{
              width: "420px",
              borderRadius: "12px",
              padding: "0",
              overflow: "hidden",
            }}
          >
            {/* MODAL HEADER */}
            <div
              style={{
                padding: "16px 20px",
                background: "#f8f9fa",
                borderBottom: "1px solid #e5e7eb",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <h3 style={{ margin: 0, fontSize: "18px", fontWeight: 600 }}>
                {editId ? "Edit User" : "Create User"}
              </h3>
              <button
                onClick={closeAllModals}
                style={{
                  border: "none",
                  background: "transparent",
                  fontSize: "20px",
                  cursor: "pointer",
                }}
              >
                ×
              </button>
            </div>

            {/* MODAL BODY */}
            <div style={{ padding: "20px" }}>
              <div className="form-group" style={{ marginBottom: "14px" }}>
                <label className="form-label">Username</label>
                <input
                  type="text"
                  name="username"
                  value={formData.username}
                  onChange={handleChange}
                  className="form-input"
                  placeholder="Enter username"
                  style={{ width: "100%" }}
                />
              </div>

              <div className="form-group" style={{ marginBottom: "14px" }}>
                <label className="form-label">Password</label>
                <input
                  type="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  className="form-input"
                  placeholder={editId ? "Leave blank to keep current password" : "Enter password"}
                  style={{ width: "100%" }}
                />
              </div>

              <div className="form-group" style={{ marginBottom: "14px" }}>
                <label className="form-label">Role</label>
                <select
                  name="role"
                  value={formData.role}
                  onChange={handleChange}
                  className="form-select"
                  style={{ width: "100%" }}
                >
                  <option value={1}>Super Admin</option>
                  <option value={2}>Admin</option>
                </select>
              </div>

              {error && (
                <div
                  style={{
                    background: "#fee2e2",
                    color: "#991b1b",
                    padding: "8px 10px",
                    borderRadius: "6px",
                    fontSize: "14px",
                    marginTop: "10px",
                  }}
                >
                  {error}
                </div>
              )}
            </div>

            {/* MODAL FOOTER */}
            <div
              style={{
                padding: "14px 20px",
                borderTop: "1px solid #e5e7eb",
                display: "flex",
                justifyContent: "flex-end",
                gap: "10px",
                background: "#f9fafb",
              }}
            >
              <button
                onClick={closeAllModals}
                style={{
                  padding: "8px 14px",
                  borderRadius: "6px",
                  border: "1px solid #d1d5db",
                  background: "#fff",
                  cursor: "pointer",
                }}
              >
                Cancel
              </button>

              <button
                onClick={handleSubmit}
                disabled={loading}
                style={{
                  padding: "8px 16px",
                  borderRadius: "6px",
                  border: "none",
                  background: "#f97316",
                  color: "#fff",
                  fontWeight: 500,
                  cursor: loading ? "not-allowed" : "pointer",
                  opacity: loading ? 0.7 : 1,
                }}
              >
                {loading ? "Saving..." : editId ? "Update User" : "Create User"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}